import type { LambdaProcessingType } from '@/types';
import { logger, ValidationError, NetworkError, ProcessingError } from '@/utils/logger';
import { cognitoAuth } from '@/services/cognitoAuth';

export interface BatchRecord {
  id: string;
  companyId: string;
  companyName: string;
  userId: string;
  filename: string;
  operation: LambdaProcessingType;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  csvDownloadUrl?: string;
  jsonDownloadUrl?: string;
  emailSent: boolean;
  createdAt: string;
  completedAt?: string;
  expiresAt?: string;
  adminEmail: string;
  errorMessage?: string;
}

export interface BatchStats {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  totalRecordsProcessed: number;
  successfulRecords: number;
}

export interface CreateBatchRequest {
  filename: string;
  operation: LambdaProcessingType;
  totalRecords: number;
  adminEmail: string;
}

export interface UpdateBatchRequest {
  status: BatchRecord['status'];
  successfulRecords?: number;
  failedRecords?: number;
  csvDownloadUrl?: string;
  jsonDownloadUrl?: string;
  emailSent?: boolean;
  expiresAt?: string;
  errorMessage?: string;
}

export class BatchHistoryAPIService {
  private apiUrl: string;

  constructor() {
    // Use account service endpoint (not batch processing endpoint)
    // Account service handles customer-facing operations like batch history
    this.apiUrl = import.meta.env.VITE_ACCOUNT_SERVICE_URL || 'https://kcboqsgpo3.execute-api.us-east-2.amazonaws.com/dev/account';

    if (!this.apiUrl) {
      throw new Error('VITE_ACCOUNT_SERVICE_URL environment variable is required');
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const currentUser = cognitoAuth.getCurrentUser();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (currentUser?.accessToken) {
      headers['Authorization'] = `Bearer ${currentUser.accessToken}`;
    }

    return headers;
  }

  private async makeAPICall(operation: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          operation,
          ...payload
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`API call failed: ${operation}`, { status: response.status, error: errorText });
        
        if (response.status === 401) {
          // Try to refresh tokens and retry once
          try {
            await cognitoAuth.refreshTokens();
            const retryResponse = await fetch(this.apiUrl, {
              method: 'POST',
              headers: this.getAuthHeaders(),
              body: JSON.stringify({
                operation,
                ...payload
              })
            });
            
            if (retryResponse.ok) {
              return await retryResponse.json();
            }
          } catch (refreshError) {
            logger.error('Token refresh failed:', refreshError);
          }
          
          throw new NetworkError('Authentication failed. Please sign in again.', 401);
        }
        
        throw new NetworkError(`API request failed: ${response.statusText}. ${errorText}`, response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }
      
      logger.error(`Network error for operation ${operation}:`, error);
      throw new NetworkError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`, 0);
    }
  }

  // Create a new batch record when processing starts
  async createBatch(batchId: string, request: CreateBatchRequest): Promise<BatchRecord> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        batchId,
        companyId: currentUser.userId, // Using userId as companyId for now
        companyName: currentUser.companyName,
        userId: currentUser.userId,
        filename: request.filename,
        operation: request.operation,
        totalRecords: request.totalRecords,
        adminEmail: request.adminEmail,
        status: 'pending' as const
      };

      logger.info('Creating batch record:', { batchId, companyName: currentUser.companyName });

      const response = await this.makeAPICall('create_batch_record', payload);
      
      if (!response.success) {
        throw new ProcessingError(`Failed to create batch record: ${response.error}`);
      }

      logger.info('Batch record created successfully:', { batchId });
      return response.batch as BatchRecord;

    } catch (error) {
      logger.error('Failed to create batch record:', error);
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      throw new ProcessingError(`Failed to create batch record: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Update batch record when processing completes
  async updateBatch(batchId: string, updates: UpdateBatchRequest): Promise<BatchRecord> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        batchId,
        companyId: currentUser.userId,
        updates
      };

      logger.info('Updating batch record:', { batchId, status: updates.status });

      const response = await this.makeAPICall('update_batch_record', payload);
      
      if (!response.success) {
        throw new ProcessingError(`Failed to update batch record: ${response.error}`);
      }

      logger.info('Batch record updated successfully:', { batchId });
      return response.batch as BatchRecord;

    } catch (error) {
      logger.error('Failed to update batch record:', error);
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      throw new ProcessingError(`Failed to update batch record: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Get batch history for the current company
  async getBatchHistory(limit?: number, offset?: number): Promise<{ batches: BatchRecord[]; total: number }> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        companyId: currentUser.userId,
        limit: limit || 50,
        offset: offset || 0
      };

      logger.info('Fetching batch history:', { companyName: currentUser.companyName, limit, offset });

      const response = await this.makeAPICall('get_batch_history', payload);

      if (!response.success) {
        throw new ProcessingError(`Failed to fetch batch history: ${response.error}`);
      }

      logger.info('Batch history fetched successfully:', { count: (response.batches as unknown[])?.length ?? 0 });
      return {
        batches: (response.batches as BatchRecord[]) ?? [],
        total: (response.total as number) ?? 0
      };

    } catch (error) {
      logger.error('Batch history API not available yet, returning empty data:', error);
      // Backend operation not implemented yet - return empty data gracefully
      return {
        batches: [],
        total: 0
      };
    }
  }

  // Get a specific batch by ID
  async getBatchById(batchId: string): Promise<BatchRecord | null> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        batchId,
        companyId: currentUser.userId
      };

      logger.info('Fetching batch by ID:', { batchId });

      const response = await this.makeAPICall('get_batch_by_id', payload);
      
      if (!response.success) {
        if (response.error === 'Batch not found') {
          return null;
        }
        throw new ProcessingError(`Failed to fetch batch: ${response.error}`);
      }

      logger.info('Batch fetched successfully:', { batchId });
      return response.batch as BatchRecord;

    } catch (error) {
      logger.error('Batch by ID API not available yet, returning null:', error);
      // Backend operation not implemented yet - return null gracefully
      return null;
    }
  }

  // Get batch statistics for the current company
  async getBatchStats(): Promise<BatchStats> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        companyId: currentUser.userId
      };

      logger.info('Fetching batch statistics:', { companyName: currentUser.companyName });

      const response = await this.makeAPICall('get_batch_stats', payload);
      
      if (!response.success) {
        throw new ProcessingError(`Failed to fetch batch statistics: ${response.error}`);
      }

      logger.info('Batch statistics fetched successfully');
      return (response.stats as BatchStats) ?? {
        total: 0,
        completed: 0,
        failed: 0,
        processing: 0,
        totalRecordsProcessed: 0,
        successfulRecords: 0
      };

    } catch (error) {
      logger.error('Batch stats API not available yet, returning empty stats:', error);
      // Backend operation not implemented yet - return empty stats gracefully
      return {
        total: 0,
        completed: 0,
        failed: 0,
        processing: 0,
        totalRecordsProcessed: 0,
        successfulRecords: 0
      };
    }
  }

  // Delete a batch record (admin only)
  async deleteBatch(batchId: string): Promise<boolean> {
    const currentUser = cognitoAuth.getCurrentUser();
    if (!currentUser) {
      throw new ValidationError('User not authenticated');
    }

    try {
      const payload = {
        batchId,
        companyId: currentUser.userId
      };

      logger.info('Deleting batch record:', { batchId });

      const response = await this.makeAPICall('delete_batch_record', payload);
      
      if (!response.success) {
        throw new ProcessingError(`Failed to delete batch: ${response.error}`);
      }

      logger.info('Batch record deleted successfully:', { batchId });
      return true;

    } catch (error) {
      logger.error('Failed to delete batch record:', error);
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      throw new ProcessingError(`Failed to delete batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export const batchHistoryAPI = new BatchHistoryAPIService();